<template>
  <div v-if="resultValues"
       class="selfgen-form"
       @mouseenter="$emit('mouseenter', $event)"
       @mouseleave="$emit('mouseleave', $event)">
    <div class="selfgen-form__item">
      <div class="selfgen-form__item-label">图形ID</div>
      <div class="selfgen-form__item-content el-input el-input--mini" style="max-width: 400px;">
        {{graphicId}}
      </div>
    </div>
    <div v-for="(f, i) in filteredFieldList" :key="i" class="selfgen-form__item">
      <div v-if="f.label" class="selfgen-form__item-label">
        {{ f.label }}
      </div>
      <component :is="f.component"
                 v-model="resultValues[f.fieldName]"
                 v-bind="f.props"
                 :disabled="disabled || f.disabled"
                 class="selfgen-form__item-content"
                 :style="f.style"
                 @change="updateResult(resultValues[f.fieldName], f)"
                 @keyup.native="preUpdate(resultValues[f.fieldName], f)">
        <slot v-if="f.inputMethod === 'select' && f.options">
          <el-option v-for="(o, j) in f.options"
                     :key="j"
                     :label="`${o.label}-${o.value}`"
                     :value="o.value">
          </el-option>
        </slot>
        <slot v-if="f.inputMethod === 'radio' && f.options">
          <el-radio v-for="(o, j) in f.options"
                    style="display: block; margin: 4px 0;"
                    :key="j"
                    :label="o.label">
          </el-radio>
        </slot>
      </component>
    </div>
  </div>
</template>

<script>
import { diff } from 'deep-object-diff';
import ImageUpload from '@/components/ImageUpload'

const componentMap = {
  input: {
    componentName: 'el-input',
    baseProps: {
      size: 'mini',
    },
    baseStyle: {
      'max-width': '400px',
    },
  },
  select: {
    componentName: 'el-select',
    baseProps: {
      size: 'mini',
    },
    baseStyle: {
      'max-width': '400px',
    },
  },
  textarea: {
    componentName: 'el-input',
    baseProps: {},
  },
  radio: {
    componentName: 'el-radio-group',
    baseProps: {},
    convertNeeded: true,
  },
  imageUpload: {
    componentName: 'image-upload',
    baseProps: {
      text: '文件大小不超过2M',
      sub: '文件格式jpg、png、jpeg、svg、gif',
      maxFileSize: 2,
      fileTypeAccept: ['jpg', 'png', 'jpeg', 'svg', 'gif'],
    },
  },
  cascader: {
    componentName: 'el-cascader',
    baseStyle: {
      'max-width': '400px',
    },
  },
};

const typeMap = {
  string: String,
  number: Number,
  boolean: Boolean,
};

export default {
  name: 'SelfGeneratedForm',
  model: {
    prop: 'result',
    event: 'change',
  },
  components: { ImageUpload },
  props: {
    fields: {
      type: Array,
      default: () => ([]),
    },
    result: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    focused: {
      type: Boolean,
      default: true,
    },
    graphicId: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    resultValues: null,
    registeredKeys: {},
  }),
  computed: {
    filteredFieldList() {
      return this.fieldList.filter((f, i) => {
        if (!this.fields[i].bindings || !this.fields[i].bindings.length) return true;
        const { bindings } = this.fields[i];
        return bindings.some(b => Object.entries(b).every(([k, v]) => this.result[k] === v));
      });
    },
    fieldList() {
      return this.fields.map((f) => {
        const inputMethod = componentMap[f.inputMethod] ? f.inputMethod : 'input';
        const settings = componentMap[inputMethod];
        const field = {};
        Object.assign(field, f);
        Object.assign(field, {
          component: settings ? settings.componentName : 'el-input',
          props: settings ? { ...f.props, ...settings.baseProps } : { ...f.props },
          style: settings ? { ...f.style, ...settings.baseStyle } : { ...f.style },
          convertNeeded: settings ? !!settings.convertNeeded : false,
        });
        this.registerKeyEvent(field);
        return field;
      });
    },
  },
  watch: {
    fields(newVal) {
      // always update field after result
      this.$nextTick(() => { this.reInitializeModels(newVal); });
    },
    result: {
      handler(newVal, oldVal) {
        if (!newVal || !Object.keys(newVal).length) {
          this.resultValues = null;
          this.reInitializeModels(this.fields);
          return;
        }
        const resultValues = {};
        const changes = {};
        this.fields.forEach((f) => {
          if (!f || newVal[f.fieldName] === undefined) return;
          const currentValue = newVal[f.fieldName];
          if (f.options && f.options.length && this.result[f.fieldName] !== null) {
            const target = f.options.find(o => o.value === this.result[f.fieldName]);
            resultValues[f.fieldName] = target ? target.label : null;
          } else {
            resultValues[f.fieldName] = this.result[f.fieldName];
          }
        });
        if (Object.keys(changes).length) this.$emit('change', Object.assign({}, newVal, changes));
        else this.resultValues = resultValues;
      },
      deep: true,
    },
    filteredFieldList(newVal, oldVal) {
      if (newVal.map(f => f.fieldName).join(',') !== oldVal.map(f => f.fieldName).join(',')) {
        this.reInitializeModels(this.fields);
      }
    },
  },
  mounted() {
    this.keyboardListener = document.addEventListener('keydown', this.onKeyDown);
    this.reInitializeModels(this.fields);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.keyboardListener);
  },
  methods: {
    reInitializeModels(newVal) {
      if (!newVal || !newVal.length) {
        this.resultValues = null;
        if (!newVal) this.$emit('change', {});
        return;
      }
      if (!this.resultValues) {
        const resultValues = {};
        newVal.forEach((f) => {
          if (this.result[f.fieldName] !== undefined && this.result[f.fieldName] !== null) {
            if (f.options && f.options.length) {
              if (f.options.find(o => o.value === this.result[f.fieldName])) {
                resultValues[f.fieldName] = f.options.find(o => o.value === this.result[f.fieldName]).label;
              } else {
                resultValues[f.fieldName] = this.result[f.fieldName];
              }
            } else {
              resultValues[f.fieldName] = this.result[f.fieldName];
            }
          }
        });
        this.resultValues = resultValues;
      }
      const resultValues = Object.assign({}, this.resultValues || {});
      this.registeredKeys = {};
      const result = newVal.reduce((r, f) => {
        if (!f) return r;
        if (resultValues[f.fieldName] !== undefined && resultValues[f.fieldName] !== null) {
          if (f.options && f.options.length) {
            const target = f.options.find(o => o.label === resultValues[f.fieldName]);
            // eslint-disable-next-line
            r[f.fieldName] = target ? target.value : (f.default !== null && f.default !== undefined ? f.default : null);
          } else {
            r[f.fieldName] = resultValues[f.fieldName];
          }
        } else {
          r[f.fieldName] = f.default !== null && f.default !== undefined ? f.default : null;
          resultValues[f.fieldName] = r[f.fieldName];
          if (this.fieldList.find(i => i.fieldName === f.fieldName).convertNeeded) {
            const target = f.options.find(o => o.value === f.default);
            resultValues[f.fieldName] = target ? target.label : null;
          }
        }
        return r;
      }, {});
      const filteredResult = {};
      this.fields.forEach((f) => {
        if (!f.bindings || !f.bindings.length) {
          filteredResult[f.fieldName] = result[f.fieldName];
          return;
        }
        if (f.bindings.some(b => Object.entries(b).every(([k, v]) => result[k] !== undefined && result[k] === v))) {
          filteredResult[f.fieldName] = result[f.fieldName];
        }
      });
      // 初次切换不emit
      // if (Object.entries(diff(filteredResult, this.result)).length) this.$emit('change', filteredResult);
      this.resultValues = resultValues;
    },
    updateResult(newValue, field) {
      const type = typeof newValue;
      let val = newValue;
      if (type !== field.dataType) {
        if (field.dataType === 'boolean') val = val === 'true';
        else val = typeMap[field.dataType](newValue);
      }
      if (field.convertNeeded) {
        val = field.options.find(o => o.label === newValue).value;
      }
      const changed = {};
      changed[field.fieldName] = val;
      this.$emit('change', Object.assign({}, this.result, changed));
    },
    preUpdate(newValue, field) {
      if (field.component !== 'el-input') return;
      this.updateResult(newValue, field);
    },
    registerKeyEvent(field) {
      if (!field.options || !Array.isArray(field.options)) return;
      if (field.options.some(i => i.key)) {
        field.options.filter(i => i.key).forEach((i) => {
          this.registeredKeys[i.key] = {
            field,
            label: i.label,
          };
        });
      }
    },
    onKeyDown(ev) {
      const code = ev.code;
      const val = this.registeredKeys[code];
      if (!val || !this.focused || this.disabled) return;
      this.resultValues[val.field.fieldName] = val.label;
      this.updateResult(val.label, val.field);
    },
  },
};
</script>

<style lang="scss" scoped>
.selfgen-form {
  color: #cccccc;
  &__item {
    display: flex;
    align-items: center;
    margin: 20px 0;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__item-label {
    min-width: 100px;
    font-size: 14px;
    font-weight: 700;

    &--inline {
      display: inline-block;
    }
  }

  &__item-content {
    flex: 1;

    &--inline {
      display: inline-block;

      & > * {
        margin-right: 10px;
      }
    }
  }

  &__item-content--inline > *:last-child {
    margin-right: 0;
  }
}
</style>
